import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FilterGroup, FilterState } from './types/filters';
import AISearchPreviewModal from './AISearchPreviewModal';
import { useTour } from './tour/TourProvider';
import { useLessonAction, tourEvent } from './tour/tourEvents';

const SKIP_PREVIEW_LS_KEY = 'aiSearch.skipPreview';

// Web Speech API types (not in all TS libs)
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface RadiusSearchResult {
  location: string;
  distance: number;
  type: 'candidate' | 'company';
}

interface DualRadiusSearchResult {
  companyRadiusSearch?: { location: string; distance: number };
  candidateRadiusSearch?: { location: string; distance: number };
}

interface SavedSearch {
  id: number;
  name: string;
  data_type: string;
  filter_state: any;
  search_term: string | null;
  radius_search: any;
  sort_config: any;
  created_at: string;
}

interface AISearchBarProps {
  onSearch: (
    filterState: FilterState,
    explanation: string,
    canWiden?: boolean,
    isWidened?: boolean,
    searchQuery?: string,
    radiusSearch?: RadiusSearchResult,
    dualRadiusSearch?: DualRadiusSearchResult
  ) => void;
  onClearFilters?: () => void;
  appliedFilterState?: FilterState;
  explanation?: string | null;
  isLoading?: boolean;
  projectId?: number;
  query?: string;
  onQueryChange?: (query: string) => void;
  canWiden?: boolean;
  isWidened?: boolean;
  onWidenSearch?: () => void;
  savedSearches?: SavedSearch[];
  onLoadSavedSearch?: (search: SavedSearch) => void;
  onDeleteSavedSearch?: (searchId: number) => void;
  // Which tab the user is currently on — passed to the backend as a hint
  // so the AI biases its filter generation toward the matching entity.
  currentDataType?: 'companies' | 'candidates' | 'managers';
}

export const AISearchBar: React.FC<AISearchBarProps> = ({
  onSearch,
  onClearFilters,
  appliedFilterState,
  explanation,
  isLoading: externalLoading = false,
  projectId,
  query: externalQuery,
  savedSearches = [],
  onLoadSavedSearch,
  onDeleteSavedSearch,
  onQueryChange,
  canWiden = false,
  isWidened = false,
  onWidenSearch,
  currentDataType
}) => {
  const [internalQuery, setInternalQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const savedDropdownRef = useRef<HTMLDivElement>(null);

  // AI search preview modal state
  interface PreviewData {
    filterGroups: FilterGroup[];
    explanation: string;
    canWiden?: boolean;
    radiusSearch?: RadiusSearchResult;
    companyRadiusSearch?: { location: string; distance: number };
    candidateRadiusSearch?: { location: string; distance: number };
    query: string;
  }
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [skipPreview, setSkipPreview] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SKIP_PREVIEW_LS_KEY) === 'true';
  });
  // Learn tab — AI Smart Search lesson.
  const { running } = useTour();
  useLessonAction('lesson-ai-search-example', () => setQuery('Find me people with experience in GenAI'));

  const updateSkipPreview = (next: boolean) => {
    setSkipPreview(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem(SKIP_PREVIEW_LS_KEY, String(next));
    }
  };

  // Close saved searches dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (savedDropdownRef.current && !savedDropdownRef.current.contains(e.target as Node)) {
        setShowSavedDropdown(false);
      }
    };
    if (showSavedDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSavedDropdown]);

  // Use external query if provided, otherwise use internal state
  const query = externalQuery !== undefined ? externalQuery : internalQuery;
  const setQuery = onQueryChange || setInternalQuery;

  // Combine internal and external loading states
  const isLoading = externalLoading || internalLoading;

  // Check browser support for Web Speech API
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  const speechSupported = !!SpeechRecognition;

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognition) return;

    // Stop any existing session
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    const recognition: SpeechRecognitionInstance = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-GB';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setQuery(transcript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
        setError(event.error === 'not-allowed' ? 'Microphone access denied. Please allow mic access in your browser.' : 'Voice recognition failed. Please try again.');
      }
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    setError(null);
    recognition.start();
  }, [SpeechRecognition, setQuery]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Single path that applies an AI search result to the table. Used by both
  // the skip-preview fast path and the modal's "Run Search" button.
  const applyAIResult = useCallback((result: {
    filterGroups: FilterGroup[];
    explanation?: string;
    canWiden?: boolean;
    radiusSearch?: RadiusSearchResult;
    companyRadiusSearch?: { location: string; distance: number };
    candidateRadiusSearch?: { location: string; distance: number };
    query: string;
  }) => {
    const filterState: FilterState = {
      groups: result.filterGroups,
      employmentTypes: []
    };
    const dualRadiusSearch: DualRadiusSearchResult | undefined =
      (result.companyRadiusSearch || result.candidateRadiusSearch)
        ? { companyRadiusSearch: result.companyRadiusSearch, candidateRadiusSearch: result.candidateRadiusSearch }
        : undefined;
    onSearch(
      filterState,
      result.explanation || '',
      result.canWiden,
      false,
      result.query,
      result.radiusSearch,
      dualRadiusSearch
    );
  }, [onSearch]);

  const handleSearch = async () => {
    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    setError(null);
    setInternalLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai-search/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
body: JSON.stringify({
  query: query.trim(),
  projectId: projectId,
  currentDataType: currentDataType
})
      });

      if (!response.ok) {
        throw new Error('AI search failed');
      }

      const data = await response.json();

      // Skip-preview mode → apply immediately, same as the original flow.
      // (Never during the AI Smart Search lesson — it walks through the preview.)
      if (skipPreview && running !== 'project-ai-search') {
        applyAIResult({
          filterGroups: data.filterGroups || [],
          explanation: data.explanation,
          canWiden: data.canWiden,
          radiusSearch: data.radiusSearch,
          companyRadiusSearch: data.companyRadiusSearch,
          candidateRadiusSearch: data.candidateRadiusSearch,
          query: query.trim()
        });
      } else {
        // Open the preview modal so the user can edit before applying.
        setPreviewData({
          filterGroups: data.filterGroups || [],
          explanation: data.explanation || '',
          canWiden: data.canWiden,
          radiusSearch: data.radiusSearch,
          companyRadiusSearch: data.companyRadiusSearch,
          candidateRadiusSearch: data.candidateRadiusSearch,
          query: query.trim()
        });
      }

    } catch (err) {
      console.error('AI Search error:', err);
      setError('AI search failed. Please try again.');
    } finally {
      setInternalLoading(false);
    }
  };

  useEffect(() => {
    if (previewData) tourEvent('ai-preview-opened');
  }, [previewData]);

  const handleClear = () => {
    setQuery('');
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSearch();
    }
  };

  return (
    <div className="border-b bg-gradient-to-r from-purple-50 to-blue-50 p-4">
      <div className="max-w-4xl">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">✨</span>
          <h3 className="text-sm font-semibold text-gray-700">AI Smart Search</h3>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              data-tour="ai-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isListening ? 'Listening... speak your search' : 'Try: "AWS contractors", "competitors of Stripe", "Python developers with 5+ years"'}
              disabled={isLoading}
              className={`w-full px-4 py-3 pr-20 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 text-sm ${isListening ? 'border-red-400 ring-2 ring-red-200' : 'border-gray-300'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {query && !isListening && (
                <button
                  onClick={handleClear}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  ✕
                </button>
              )}
              {speechSupported && (
                <button
                  onClick={isListening ? stopListening : startListening}
                  disabled={isLoading}
                  className={`p-1.5 rounded-full transition-colors ${isListening ? 'bg-red-100 text-red-600 animate-pulse' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'} disabled:opacity-50`}
                  title={isListening ? 'Stop listening' : 'Voice search'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <rect x="9" y="2" width="6" height="11" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          <button
            data-tour="ai-search-run"
            onClick={handleSearch}
            disabled={isLoading || !query.trim()}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-sm flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="animate-spin">⏳</span>
                Searching...
              </>
            ) : (
              <>
                <span>🔍</span>
                Search
              </>
            )}
          </button>

          {/* Saved Searches Button */}
          {savedSearches.length > 0 && onLoadSavedSearch && (
            <div className="relative" ref={savedDropdownRef}>
              <button
                onClick={() => setShowSavedDropdown(!showSavedDropdown)}
                className={`px-3 py-3 border rounded-lg text-sm flex items-center gap-1.5 transition-colors ${showSavedDropdown ? 'bg-purple-100 border-purple-400 text-purple-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700'}`}
                title="Saved searches"
                data-tour="saved-searches-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <span className="text-xs font-medium">{savedSearches.length}</span>
              </button>

              {showSavedDropdown && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Saved Searches</span>
                  </div>
                  {savedSearches.map((search) => (
                    <div
                      key={search.id}
                      className="px-3 py-2.5 hover:bg-purple-50 cursor-pointer border-b border-gray-50 last:border-b-0 group flex items-center justify-between"
                    >
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => {
                          onLoadSavedSearch(search);
                          setShowSavedDropdown(false);
                        }}
                      >
                        <div className="text-sm font-medium text-gray-800 truncate">{search.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                          {search.filter_state?.groups?.length > 0 && (
                            <span>{search.filter_state.groups.length} filter{search.filter_state.groups.length > 1 ? 's' : ''}</span>
                          )}
                          {search.radius_search && (
                            <span>{search.radius_search.distance}mi of {search.radius_search.location}</span>
                          )}
                          {search.filter_state?.employmentTypes?.length > 0 && (
                            <span>{search.filter_state.employmentTypes.join(', ')}</span>
                          )}
                        </div>
                      </div>
                      {onDeleteSavedSearch && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete saved search "${search.name}"?`)) {
                              onDeleteSavedSearch(search.id);
                            }
                          }}
                          className="ml-2 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
            <span>⚠️</span>
            {error}
          </div>
        )}

{explanation && (
  <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
    <div className="flex items-start gap-2">
      <span className="text-purple-600 mt-0.5">💡</span>
      <div className="flex-1">
        <p className="text-sm text-gray-700">{explanation}</p>
              </div>
              {canWiden && onWidenSearch && (
                <button
                  data-tour="widen-search"
                  onClick={onWidenSearch}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-xs whitespace-nowrap"
                >
                  🔍 Widen Search
                </button>
              )}
            </div>
            {isWidened && (
              <div className="mt-2 text-xs text-purple-600 flex items-center gap-1">
                <span>✓</span>
                Using widened search criteria (more results, looser matching)
              </div>
            )}
          </div>
        )}

{appliedFilterState && appliedFilterState.groups.length > 0 && (
          <div className="mt-3 space-y-2" data-tour="ai-search-summary">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">🤖 AI Generated Search:</span>
              {onClearFilters && (
                <button
                  onClick={onClearFilters}
                  className="px-3 py-1 text-xs text-red-600 hover:text-red-800 font-medium flex items-center gap-1 hover:bg-red-50 rounded"
                >
                  <span>✕</span>
                  Clear AI Search
                </button>
              )}
            </div>
            
            {appliedFilterState.groups.map((group) => (
              <div 
                key={group.id}
                className="bg-purple-50 border border-purple-200 rounded-lg p-3"
              >
                <div className="text-xs font-medium text-purple-900 mb-2">
                  {group.name || 'Filter Group'} ({group.logic} logic)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {group.conditions.map((condition) => {
                    const displayValue = condition.values
                      ? condition.values.join(' + ')
                      : condition.value;
                    return (
                      <span
                        key={condition.id}
                        className="px-2 py-1 bg-white text-purple-800 rounded text-xs font-medium border border-purple-300"
                      >
                        {condition.column} {condition.operator} "{displayValue}"
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
            
            <div className="bg-blue-50 border border-blue-200 rounded p-2 flex items-start gap-2">
              <span className="text-blue-600 text-sm">💡</span>
              <div className="text-xs text-blue-800">
                <strong>These filters are now active.</strong> Click "🔍 Filters" below to view, edit, or add more filter groups.
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <div>
            <span className="font-medium">Examples:</span> "AWS DevOps engineers in Manchester" • "competitors of Checkout.com" • "Contract Python developers" • "Senior managers in healthcare"
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500 select-none cursor-pointer ml-3 whitespace-nowrap">
            <input
              type="checkbox"
              checked={skipPreview}
              onChange={e => updateSkipPreview(e.target.checked)}
              className="h-3 w-3 accent-blue-600"
            />
            Skip preview
          </label>
        </div>
      </div>

      <AISearchPreviewModal
        isOpen={previewData !== null}
        onClose={() => setPreviewData(null)}
        filterGroups={previewData?.filterGroups || []}
        query={previewData?.query || ''}
        explanation={previewData?.explanation}
        radiusSearch={previewData?.radiusSearch}
        companyRadiusSearch={previewData?.companyRadiusSearch}
        candidateRadiusSearch={previewData?.candidateRadiusSearch}
        projectId={projectId}
        onApply={(editedGroups) => {
          if (!previewData) return;
          applyAIResult({ ...previewData, filterGroups: editedGroups });
          setPreviewData(null);
        }}
      />
    </div>
  );
};
